import React, { useState, useEffect } from 'react';
import { Search, CheckCircle2, Clock, XCircle, RefreshCw, Loader2, Download } from 'lucide-react';
import api from '../../utils/api';
import RefundPhoneConfirmModal from './RefundPhoneConfirmModal';
import PartialRefundModal from './PartialRefundModal';
import RefundLogsModal from './RefundLogsModal';
import PackageSearchSelect from './PackageSearchSelect';

const UNMATCHED_LABEL_VALUE = '__UNMATCHED__';

const PAY_TYPE_OPTIONS = [
  { value: 'all', label: '全部支付方式' },
  { value: 'wechat', label: '微信支付' },
  { value: 'alipay', label: '支付宝' },
  { value: 'hzbank', label: '联合银行' },
  { value: 'ly360', label: '360支付' },
  { value: 'ly360top', label: '360支付(top)' },
  { value: 'aliyun_market', label: '阿里云市场' }
];

const AMOUNT_OPTIONS = [
  { value: 'all', label: '全部金额' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: '500', label: '500' },
  { value: 'exclude_regular_excl_99', label: '去除常规金额(排除9.9)' },
  { value: 'exclude_regular_incl_99', label: '去除常规金额(包含9.9)' },
  { value: 'custom', label: '自定义金额…' }
];

export default function RefundManagementTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [refunding, setRefunding] = useState(false);
  const [filterStatus, setFilterStatus] = useState('1'); // 默认显示已支付
  const [filterPayType, setFilterPayType] = useState('all');
  const [filterOrderKind, setFilterOrderKind] = useState('all'); // 订单类型：all=全部 one_time=一次性 autopay=自动续费
  const [filterAmount, setFilterAmount] = useState('all');
  const [customAmount, setCustomAmount] = useState(''); // 自定义金额生效值（仅在 filterAmount === 'custom' 时使用）
  const [customAmountDraft, setCustomAmountDraft] = useState(''); // 自定义金额输入框草稿
  const [filterPhone, setFilterPhone] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterUserLabel, setFilterUserLabel] = useState('');
  const [labelOptions, setLabelOptions] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, order: null, mode: 'refund' });
  const [partialRefundDialog, setPartialRefundDialog] = useState({ open: false, order: null });
  const [refundLogsOrder, setRefundLogsOrder] = useState(null);
  const [jumpPage, setJumpPage] = useState('');

  const fetchOrders = async ({
    page = 1,
    status = filterStatus,
    phone = filterPhone,
    payType = filterPayType,
    orderKind = filterOrderKind,
    amount = filterAmount,
    userLabel = filterUserLabel,
    custom = customAmount
  } = {}) => {
    const effectiveAmount = amount === 'custom' ? custom : amount;
    setLoading(true);
    try {
      const response = await api.get(`/admin/payments`, {
        params: {
          page,
          limit: pagination.limit,
          status: status !== 'all' ? status : undefined,
          phone: phone || undefined,
          payType: payType !== 'all' ? payType : undefined,
          orderKind: orderKind !== 'all' ? orderKind : undefined,
          amount: effectiveAmount !== 'all' && effectiveAmount !== '' ? effectiveAmount : undefined,
          userLabel: userLabel || undefined
        }
      });
      if (response.data.success) {
        const data = response.data.data || response.data;
        setOrders(data.list || []);
        setLabelOptions(Array.isArray(data.labelOptions) ? data.labelOptions : []);
        setPagination({
          page: data.page,
          limit: data.limit,
          total: data.total,
          totalPages: data.totalPages
        });
      }
    } catch (error) {
      console.error('Failed to fetch payment orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders({
      page: 1,
      status: filterStatus,
      phone: filterPhone,
      payType: filterPayType,
      orderKind: filterOrderKind,
      amount: filterAmount,
      userLabel: filterUserLabel,
      custom: customAmount
    });
  }, [filterStatus, filterPayType, filterOrderKind, filterAmount, filterPhone, filterUserLabel, customAmount]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilterPhone(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    setFilterPhone('');
    setFilterStatus('1');
    setFilterPayType('all');
    setFilterOrderKind('all');
    setFilterAmount('all');
    setCustomAmount('');
    setCustomAmountDraft('');
    setFilterUserLabel('');
    setJumpPage('');
  };

  const handleJump = () => {
    const page = Number(jumpPage);
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('');
      return;
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`);
      return;
    }
    fetchOrders({ page });
    setJumpPage('');
  };

  // 自定义金额提交：合法（数字，最多两位小数）才生效，非法输入恢复为已生效值
  const commitCustomAmount = () => {
    const value = customAmountDraft.trim();
    if (value === '') {
      setCustomAmount('');
      return;
    }
    if (/^\d+(\.\d{1,2})?$/.test(value)) {
      setCustomAmount(value);
    } else {
      setCustomAmountDraft(customAmount);
    }
  };

  const openConfirmDialog = (order, mode) => {
    setConfirmDialog({ open: true, order, mode });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, order: null, mode: 'refund' });
  };

  const handleRefundConfirm = async (confirmPhone) => {
    const order = confirmDialog.order;
    if (!order?.order_no) return;

    setRefunding(true);
    try {
      const res = await api.post('/payment/admin/refund', {
        orderNo: order.order_no,
        confirmPhone
      });
      if (res.data.success) {
        alert('退款成功');
        closeConfirmDialog();
        fetchOrders({ page: pagination.page });
      }
    } catch (error) {
      console.error('退款失败:', error);
      alert(error.response?.data?.error || '退款处理出现异常');
    } finally {
      setRefunding(false);
    }
  };

  const openPartialRefundDialog = (order) => {
    setPartialRefundDialog({ open: true, order });
  };

  const closePartialRefundDialog = () => {
    setPartialRefundDialog({ open: false, order: null });
  };

  const handlePartialRefundConfirm = async (refundAmount, confirmPhone, remark) => {
    const order = partialRefundDialog.order;
    if (!order?.order_no) return;

    setRefunding(true);
    try {
      const res = await api.post('/payment/admin/refund-partial', {
        orderNo: order.order_no,
        refundAmount,
        confirmPhone,
        remark
      }, { timeout: 60000 });
      if (res.data.success) {
        alert(res.data.message || '部分退款成功');
        closePartialRefundDialog();
        fetchOrders({ page: pagination.page });
      }
    } catch (error) {
      console.error('部分退款失败:', error);
      alert(error.response?.data?.error || '部分退款处理出现异常');
    } finally {
      setRefunding(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1"/> 待支付</span>;
      case 1:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1"/> 已支付</span>;
      case 2:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1"/> 已关闭</span>;
      case 3:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"><RefreshCw className="w-3 h-3 mr-1"/> 已退款</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">未知</span>;
    }
  };

  // 订单类型：一次性 / 自动续费·首期（签约单）/ 自动续费·代扣
  const renderPurchaseKind = (kind) => {
    if (kind === 'autopay_sign') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><RefreshCw className="w-3 h-3 mr-1"/> 自动续费·首期</span>;
    }
    if (kind === 'autopay_charge') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"><RefreshCw className="w-3 h-3 mr-1"/> 自动续费·代扣</span>;
    }
    return <span className="text-xs text-gray-400">一次性</span>;
  };

  const renderUserLabels = (labels) => {
    const normalizedLabels = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (normalizedLabels.length <= 0) {
      return <span className="text-xs text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-col items-start gap-2">
        {normalizedLabels.map((label) => (
          <span
            key={label}
            className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  const labelSelectOptions = [
    { id: 'refund-label-unmatched', value: UNMATCHED_LABEL_VALUE, label: '未匹配' },
    ...labelOptions.map((label, index) => ({
      id: `refund-label-${index}`,
      value: label,
      label
    }))
  ];

  const getPayTypeLabel = (payType) => {
    if (payType === 'wechat') return '微信支付';
    if (payType === 'alipay') return '支付宝';
    if (payType === 'hzbank') return '联合银行';
    if (payType === 'ly360') return '360支付';
    if (payType === 'ly360top') return '360支付(top)';
    if (payType === 'aliyun_market') return '阿里云市场';
    return payType || '-';
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/payments/export-csv', {
        params: {
          status: filterStatus !== 'all' ? filterStatus : undefined,
          phone: filterPhone || undefined,
          payType: filterPayType !== 'all' ? filterPayType : undefined,
          orderKind: filterOrderKind !== 'all' ? filterOrderKind : undefined,
          amount: filterAmount === 'custom' ? (customAmount || undefined) : (filterAmount !== 'all' ? filterAmount : undefined),
          userLabel: filterUserLabel || undefined
        },
        responseType: 'blob',
        timeout: 0
      });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const rawFilename = utf8Match?.[1] || plainMatch?.[1] || `已支付订单_${new Date().toISOString().slice(0, 10)}.csv`;
      const filename = decodeURIComponent(rawFilename);

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('导出已支付订单失败:', error);
      alert('导出失败，请稍后重试');
    }
  };

  const hasActiveFilters = !!filterPhone || filterStatus !== '1' || filterPayType !== 'all' || filterOrderKind !== 'all'
    || (filterAmount !== 'all' && !(filterAmount === 'custom' && !customAmount)) || !!filterUserLabel;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">已支付订单</h2>
        
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* 状态筛选 */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
          >
            <option value="all">全部状态</option>
            <option value="0">待支付</option>
            <option value="1">已支付 (可退款)</option>
            <option value="2">已关闭</option>
            <option value="3">已退款</option>
          </select>

          <select
            value={filterPayType}
            onChange={(e) => setFilterPayType(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
          >
            {PAY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={filterOrderKind}
            onChange={(e) => setFilterOrderKind(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
          >
            <option value="all">全部类型</option>
            <option value="one_time">一次性</option>
            <option value="autopay">自动续费</option>
          </select>

          <select
            value={filterAmount}
            onChange={(e) => {
              const value = e.target.value;
              setFilterAmount(value);
              if (value === 'custom') setCustomAmountDraft(customAmount);
            }}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
          >
            {AMOUNT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          {filterAmount === 'custom' && (
            <input
              type="text"
              inputMode="decimal"
              placeholder="输入金额(元)"
              value={customAmountDraft}
              onChange={(e) => setCustomAmountDraft(e.target.value)}
              onBlur={commitCustomAmount}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitCustomAmount();
                }
              }}
              className="w-28 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
            />
          )}

          <PackageSearchSelect
            value={filterUserLabel}
            onChange={(value) => setFilterUserLabel(value)}
            options={labelSelectOptions}
            placeholder="全部标签"
            className="w-full sm:w-52 min-w-0"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
            searchPlaceholder="搜索用户标签"
          />

          {/* 手机号/设备号搜索 */}
          <form onSubmit={handleSearch} className="flex flex-1 sm:flex-none items-center gap-2">
            <div className="relative flex-1 sm:w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="搜索手机号/设备号"
                className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm whitespace-nowrap"
              >
                重置
              </button>
            )}
          </form>

          <button
            onClick={() => fetchOrders({ page: pagination.page })}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ml-auto sm:ml-0"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">刷新</span>
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">导出 CSV</span>
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">订单号/流水号</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">付款用户</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">标签</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">渠道来源</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">支付金额</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">支付方式</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">订单类型</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">会员时长</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">暂无订单数据</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{order.order_no}</div>
                      <div className="text-xs text-gray-500">{order.transaction_id || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white truncate max-w-[120px]" title={order.nickname || order.username}>{order.nickname || order.username}</div>
                      <div className="text-xs text-gray-500">{order.phone || '-'}</div>
                      <div className="text-xs text-gray-500">ID: {order.user_id}</div>
                    </td>
                    <td className="px-4 py-4">
                      {renderUserLabels(order.user_labels)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {order.channel_label || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div
                        className="text-sm font-bold text-red-600"
                        title={Number(order.refunded_amount || 0) > 0 ? `已退 ¥${Number(order.refunded_amount).toFixed(2)}` : undefined}
                      >
                        ¥{Number(order.amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getPayTypeLabel(order.pay_type)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {renderPurchaseKind(order.purchase_kind)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.membership_duration_text || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()}<br/>
                      {new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {order.status === 1 || order.status === 3 || Number(order.refunded_amount || 0) > 0 ? (
                        <div className="flex justify-end gap-3">
                          {order.status === 1 && Number(order.refunded_amount || 0) <= 0 && (
                            <button
                              onClick={() => openConfirmDialog(order, 'refund')}
                              disabled={refunding}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                              退款扣除余额
                            </button>
                          )}
                          {order.status === 1 && (order.pay_type === 'wechat' || order.pay_type === 'alipay') && (
                            <button
                              onClick={() => openPartialRefundDialog(order)}
                              disabled={refunding}
                              className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                            >
                              部分退款
                            </button>
                          )}
                          {(order.status === 3 || Number(order.refunded_amount || 0) > 0) && (
                            <button
                              onClick={() => setRefundLogsOrder(order)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              退款记录
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              共 {pagination.total} 条记录
            </span>
            <div className="flex gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                onClick={() => fetchOrders({ page: pagination.page - 1 })}
                disabled={pagination.page === 1 || loading}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm disabled:opacity-50"
              >
                上一页
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchOrders({ page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages || loading}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      <RefundPhoneConfirmModal
        open={confirmDialog.open}
        title="确认退款扣除余额"
        description={
          String(confirmDialog.order?.phone || '').trim()
            ? '请再次输入该订单用户手机号，确认无误后才能执行【退款扣除余额】。'
            : '该订单用户未绑定手机号（设备用户），请再次输入该用户ID，确认无误后才能执行【退款扣除余额】。'
        }
        order={confirmDialog.order}
        actionText="确认退款"
        submitting={refunding}
        onClose={closeConfirmDialog}
        onConfirm={handleRefundConfirm}
      />

      <PartialRefundModal
        open={partialRefundDialog.open}
        order={partialRefundDialog.order}
        submitting={refunding}
        onClose={closePartialRefundDialog}
        onConfirm={handlePartialRefundConfirm}
      />

      <RefundLogsModal
        open={!!refundLogsOrder}
        order={refundLogsOrder}
        onClose={() => setRefundLogsOrder(null)}
      />
    </div>
  );
}
